// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { useMemo, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  ButtonGroup,
  Input,
  ListGroup,
  ListGroupItem
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import DefaultDash from "../../main";
import { Colxx } from "Components/CustomBootstrap";
import IntlMessages from "Util/IntlMessages";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell
} from "../../_suite/primitives";

function MainNotificationsFeedPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.main.listNotifications(),
    []
  );
  const [cat, setCat] = useState("all");
  const categories = useMemo(() => {
    const list = data || [];
    const s = new Set(list.map(n => n.category).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [data]);

  const { unread, read } = useMemo(() => {
    const list = data || [];
    const byCat = row =>
      cat === "all" || row.category === cat;
    return {
      unread: list.filter(n => !n.read && byCat(n)),
      read: list.filter(n => n.read && byCat(n))
    };
  }, [data, cat]);

  return (
    <SuitePageShell
      heading={<IntlMessages id="suite.main.notifications" />}
      afterBreadcrumb={
        <p className="text-muted small mb-0">
          Inbox-style feed — pair with WebSocket or polling for live updates.
        </p>
      }
    >
      <div className="d-flex flex-wrap gap-2 mb-4">
        <ButtonGroup size="sm" className="flex-wrap">
          {categories.map(c => (
            <Button
              key={c}
              color={cat === c ? "primary" : "light"}
              onClick={() => setCat(c)}
            >
              {c === "all" ? "All" : c}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <AsyncStateBoundary loading={loading} error={error} empty={false}>
        <Row>
          <Col lg="7" className="mb-4">
            <h6 className="text-uppercase text-muted mb-3">New</h6>
            <ListGroup className="shadow-sm">
              {unread.map(n => (
                <ListGroupItem
                  key={n.id}
                  className="d-flex justify-content-between align-items-start"
                >
                  <div>
                    {n.category && (
                      <Badge color="light" className="text-dark me-2 mb-1">
                        {n.category}
                      </Badge>
                    )}
                    <strong>{n.title}</strong>
                    <div className="small text-muted">{n.time}</div>
                    {n.subtitle && (
                      <div className="small text-muted">{n.subtitle}</div>
                    )}
                  </div>
                  <Badge color="primary" pill>
                    New
                  </Badge>
                </ListGroupItem>
              ))}
              {unread.length === 0 && (
                <ListGroupItem className="text-muted small">
                  You&apos;re caught up.
                </ListGroupItem>
              )}
            </ListGroup>
          </Col>
          <Col lg="5">
            <h6 className="text-uppercase text-muted mb-3">Earlier</h6>
            <ListGroup>
              {read.map(n => (
                <ListGroupItem key={n.id} className="text-muted small">
                  {n.category && (
                    <Badge color="light" className="text-dark me-1">
                      {n.category}
                    </Badge>
                  )}
                  <div>{n.title}</div>
                  <div>{n.time}</div>
                  {n.subtitle && <div>{n.subtitle}</div>}
                </ListGroupItem>
              ))}
            </ListGroup>
          </Col>
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function MainSearchGroupedPage() {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.main.search(q),
    [q]
  );

  const grouped = useMemo(() => {
    const list = data || [];
    const m = {};
    list.forEach(h => {
      const t = h.type || "Other";
      if (typeFilter !== "all" && t !== typeFilter) return;
      if (!m[t]) m[t] = [];
      m[t].push(h);
    });
    return m;
  }, [data, typeFilter]);

  const types = useMemo(() => {
    const list = data || [];
    return ["all", ...Array.from(new Set(list.map(h => h.type || "Other")))];
  }, [data]);

  return (
    <SuitePageShell heading={<IntlMessages id="suite.main.search" />}>
      <Input
        type="search"
        className="mb-3"
        placeholder="Search workspace…"
        value={q}
        onChange={e => setQ(e.target.value)}
      />
      <div className="d-flex flex-wrap gap-2 mb-4">
        <ButtonGroup size="sm" className="flex-wrap">
          {types.map(t => (
            <Button
              key={t}
              color={typeFilter === t ? "primary" : "light"}
              onClick={() => setTypeFilter(t)}
            >
              {t}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <AsyncStateBoundary
        loading={loading}
        error={error}
        empty={!loading && Object.keys(grouped).length === 0}
      >
        {Object.entries(grouped).map(([type, hits]) => (
          <Card key={type} className="mb-4 border-0 shadow-sm">
            <CardBody>
              <CardTitle tag="h5" className="text-primary">
                {type}
              </CardTitle>
              <ul className="list-unstyled mb-0">
                {hits.map(h => (
                  <li key={h.id} className="py-2 border-bottom">
                    <Link to={h.path}>{h.title}</Link>
                    <span className="text-muted small ms-2">{h.path}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function MainShortcutsChipsPage() {
  const { data, loading } = useAsyncResource(() => suiteApi.main.getShortcuts(), []);
  const links = data?.links || [];

  return (
    <SuitePageShell heading={<IntlMessages id="suite.main.shortcuts" />}>
      <p className="text-muted small mb-4">
        Personal launcher tiles — persist order via user preferences API.
      </p>
      <AsyncStateBoundary loading={loading} error={null} empty={!links.length}>
        <Row>
          {links.map((l, i) => (
            <Col xs="6" md="4" lg="3" key={i} className="mb-3">
              <Link
                to={l.to}
                className="text-decoration-none d-block h-100 p-4 rounded bg-light text-center border hover-shadow"
              >
                <i className="simple-icon-arrow-right-circle text-primary d-block mb-2 h1" />
                <span className="d-block font-weight-bold">
                  <IntlMessages id={l.labelId} />
                </span>
                {l.subtitleId && (
                  <span className="d-block small text-muted mt-2">
                    <IntlMessages id={l.subtitleId} />
                  </span>
                )}
              </Link>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function MainSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="main" />}>
        <Route index element={<DefaultDash />} />
        <Route path="notifications" element={<MainNotificationsFeedPage />} />
        <Route path="search" element={<MainSearchGroupedPage />} />
        <Route path="shortcuts" element={<MainShortcutsChipsPage />} />
      </Route>
    </Routes>
  );
}
